import { constrainedForecastViewInterface } from 'Interfaces/FDPInterfaces/constrainedForecast';
import {
  conceptInformationInterfaceWithNdcInformation,
  notesInterface,
  singleConceptInterface,
  singleContractInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { invoiceSalesSummary } from 'Interfaces/FDPInterfaces/unconstrainedForecast';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { constrainedForecastService } from 'services/fdp/constrainedForecastService';
import { unconstrainedForecastService } from 'services/fdp/unconstrainedForecastService';
import { tmsScreenServices } from 'services/tmsScreenServices';
import { convertPlanningPeriodArray } from 'utils/dateUtilities';
import { checkIfStringIsValidNdc } from 'utils/ndcUtilities';

export class ConstrainedForecastViewStore {
  directcurrentNDCIndex: number = 0;
  indirectcurrentNDCIndex: number = 0;
  isAllConceptLoading: boolean = false;
  isConstrainedForecastLoading: boolean = false;
  filteredConceptsForSearch: string[] = [];
  allDrugMoleculesDetailsForSearch: conceptInformationInterfaceWithNdcInformation[] = [];
  filteredDrugMoleculesDetails: conceptInformationInterfaceWithNdcInformation[] = [];
  conceptSearchInput: string = '';
  isConceptLoading: boolean = false;
  isConceptDetailsLoading: boolean = false;
  conceptDetails: singleConceptInterface | null = {
    concept: '',
    gfc: 0,
    generic_rxcui: '',
    therapeutic_category: '',
    pharmacologic_class: '',
    va_class_name: '',
    des_schedule: '',
    duration_condition: '',
    Strength: '',
  };
  contractDetails: singleContractInterface[] | [] = [
    {
      GFC: 0,
      ContractNumber: '',
      ContractType: '',
      StartDate: '',
      EndDate: '',
    },
  ];
  directNDCheading: { ndc: string; packSize: string }[] = [];
  indirectNDCheading: { ndc: string; packSize: string }[] = [];
  constrainedForecastData: constrainedForecastViewInterface = {
    GFC: 0,
    PlanningPeriod: '',
    forecasts: [
      {
        NDC11: '',
        PackSize: 0,
        SellingCategory: '',
        forecast_details: [
          {
            ForecastPeriod: '',
            Adjustment: 0,
            UnconstrainedForecast: 0,
            ConstrainedForecast: 0,
            Notes: '',
          },
        ],
      },
    ],
  };
  isInvoiceSalesLoading: boolean = false;
  invoiceSalesSummary: invoiceSalesSummary[] = [];
  isBulkUpdateModalOpen: boolean = false;
  bulkNDCdata: {
    Status: boolean;
    Concept_info: {
      Concept: string;
      NDC11_list: {
        NDC11: string;
      }[];
    }[];
  }[] = [];

  bulkUpdateDirectTodoNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateIndirectTodoNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateDirectCompletedNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateIndirectCompletedNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateNDCs: string[] = [];
  isForecastBulkUpdating: boolean = false;
  isConstrainedForecastNotesDataLoading: boolean = false;
  ConstrainedForecastNotesData: notesInterface[] = [];
  isUnconstrainedForecastProgressLoading: boolean = false;
  percentProgress: { percentage: number } = { percentage: 0 };
  constrainedForecastStatus: boolean = false;
  isConstrainedForecastStatusLoading: boolean = false;
  constrainedFOrecastPlanningPeriodLoading: boolean = false;
  constrainedForecastPlanningPeriods: { label: string; code: string }[] = [];
  constrainedForecastPeriod: { label: string; code: string } = {
    label: '',
    code: '',
  };
  lastNdcValueEntered: string = '';
  forecastBtnLabel: string = 'New Forecast';

  constructor() {
    makeAutoObservable(this);
  }
  setSearchInput = (value: string) => {
    this.conceptSearchInput = value;
  };

  setIsConceptDetailsLoading = (flag: boolean) => {
    this.isConceptDetailsLoading = flag;
  };

  setDirectCurrentTabIndex = (newIndex: number) => {
    this.directcurrentNDCIndex = newIndex;
  };

  setIndirectCurrentTabIndex = (newIndex: number) => {
    this.indirectcurrentNDCIndex = newIndex;
  };

  // Filter function with custom debounce
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  handleFetchAllDetailsByGFC = async (gfc: string) => {
    this.isAllConceptLoading = true;
    try {
      this.fetchForecastProgress();
      const data = await tmsScreenServices.getAllActiveGSMSConceptsWithNdcInformation();
      this.fetchBulkUpdateNDCs();

      runInAction(() => {
        this.allDrugMoleculesDetailsForSearch = data;
        this.filteredDrugMoleculesDetails = data;
      });

      const [moleculeForGfcParam] = this.allDrugMoleculesDetailsForSearch.filter(
        drug => drug.GFC === parseInt(gfc)
      );
      if (moleculeForGfcParam) {
        this.conceptSearchInput = moleculeForGfcParam.Concept;
        this.handleSearch();
      } else {
        toast.error(`There is no molecule corresponding to gfc - ${gfc}`);
      }
    } catch (err) {
      console.error('Error while getting all drug details:', err);
    } finally {
      runInAction(() => {
        this.isAllConceptLoading = false;
      });
    }
  };

  handleFetchAllConceptDetailsForSearch = async () => {
    this.isAllConceptLoading = true;
    try {
      this.fetchForecastProgress();
      const data = await tmsScreenServices.getAllActiveGSMSConceptsWithNdcInformation();
      this.fetchBulkUpdateNDCs();
      runInAction(() => {
        this.allDrugMoleculesDetailsForSearch = data;
        this.filteredDrugMoleculesDetails = data;
      });
    } catch (err) {
      console.error('Error while getting all drug details:', err);
    } finally {
      runInAction(() => {
        this.isAllConceptLoading = false;
      });
    }
  };

  private debounce<T extends unknown[]>(func: (...args: T) => void, delay: number) {
    return (...args: T) => {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  handleFilterConceptSearch = this.debounce(() => {
    if (!this.conceptSearchInput.trim()) {
      this.filteredConceptsForSearch = [];
      return;
    }

    if (checkIfStringIsValidNdc(this.conceptSearchInput)) {
      const filteredNdc = this.allDrugMoleculesDetailsForSearch.filter(
        item => item.NDC11 === this.conceptSearchInput.trim()
      );
      if (filteredNdc.length === 1) {
        this.filteredConceptsForSearch = [`${filteredNdc[0].Concept} (NDC)`];
      }
      return;
    }

    const searchText = this.conceptSearchInput.toLowerCase();
    runInAction(() => {
      this.filteredConceptsForSearch = this.allDrugMoleculesDetailsForSearch
        .filter(concept => concept.Concept.toLowerCase().includes(searchText))
        .slice(0, 10)
        .map(concept => concept.Concept); // Extract only the `concept` string

      this.filteredConceptsForSearch = [...new Set(this.filteredConceptsForSearch)];
    });
  }, 300);

  handleConceptSearchInputChange = (input: string) => {
    if (checkIfStringIsValidNdc(input)) {
      this.lastNdcValueEntered = input;
    }
    this.conceptSearchInput = input;
    this.handleFilterConceptSearch();
  };

  getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth()).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  handleGetConceptDetails = async (gfc: number) => {
    runInAction(() => {
      this.isConceptLoading = true;
      // this.isConstrainedForecastNotesDataLoading = true;
      this.ConstrainedForecastNotesData = [];
      this.isConstrainedForecastLoading = true;
      this.constrainedFOrecastPlanningPeriodLoading = true;
      this.directNDCheading = [];
      this.indirectNDCheading = [];
      this.constrainedForecastPeriod = { label: '', code: '' };
      this.constrainedForecastPlanningPeriods = [];
    });
    try {
      // await this.fetchConstrainedForecastProgress(gfc);
      const data = await tmsScreenServices.getSingleGSMSConcept(gfc);
      runInAction(() => {
        this.conceptDetails = data.concept_info[0];
        this.contractDetails = data.contract_info;
      });

      if (this.conceptDetails?.gfc) {
        this.fetchConstrainedForecastPlanningPeriod(gfc);
        this.getInvoiceSalesSummary(this.conceptDetails?.gfc);
      }
    } catch (error) {
      console.error('Error while getting concept details:', error);
    } finally {
      runInAction(() => {
        this.isConceptLoading = false;
      });
    }
  };

  handleSearch = async () => {
    if (!this.conceptSearchInput) return;

    const matchedMolecules = this.allDrugMoleculesDetailsForSearch.filter(
      concept => concept.Concept === this.conceptSearchInput
    );

    if (matchedMolecules.length === 0) {
      toast.error('No concept found with provided name');
    }

    const currURL = window.location.href;
    const url_check = currURL.split('/').includes('edit');
    if (url_check) {
      window.location.href = `/fdp/constrained_forecast?gfc=${matchedMolecules[0].GFC}`;
    }

    if (matchedMolecules.length > 0) {
      const selectedConcept = matchedMolecules[0];
      this.handleGetConceptDetails(selectedConcept.GFC);
    } else {
      this.isConceptDetailsLoading = false;
    }
  };

  handleEnterKeyDownConceptSearchField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  };

  fetchConstrainedForecastProgress = async (gfc: number) => {
    this.isConstrainedForecastStatusLoading = true;
    try {
      const data = await constrainedForecastService.getConstrainedForecastStatus(gfc);

      if (data && data.length > 0) {
        let constrainedStatus: boolean = true;
        let matchedNDCCount: number = 0;
        const directNDCSet = new Set(this.directNDCheading.map(item => item.ndc));
        const indirectNDCSet = new Set(this.indirectNDCheading.map(item => item.ndc));
        for (const forecast of data) {
          if (!forecast.ConstrainedForecastStatus) {
            constrainedStatus = false;
          }
          if (directNDCSet.has(forecast.NDC11) || indirectNDCSet.has(forecast.NDC11)) {
            matchedNDCCount++;
          }
        }
        const allMatched = matchedNDCCount === data.length;
        if (allMatched && !constrainedStatus) {
          this.forecastBtnLabel = 'New Forecast';
        } else if (allMatched && constrainedStatus) {
          this.forecastBtnLabel = 'Edit Forecast';
        } else if (!allMatched && !constrainedStatus) {
          this.forecastBtnLabel = 'Continue Forecast';
        }
        this.constrainedForecastStatus = constrainedStatus;
      }
    } catch (error) {
      console.log('constrainedForecastViewScreenStore fetchForecastProgress => ', error);
    } finally {
      this.isConstrainedForecastStatusLoading = false;
    }
  };

  fetchForecastProgress = async () => {
    this.isUnconstrainedForecastProgressLoading = true;
    try {
      const data = await constrainedForecastService.getConstrainedForecastProgress();
      this.percentProgress = data;
    } catch (error) {
      console.log('ConstrainedForecastViewScreenStore fetchForecastProgress => ', error);
    }
  };

  fetchConstrainedForecastPlanningPeriod = async (gfc: number) => {
    this.isConstrainedForecastStatusLoading = true;
    try {
      const data = await constrainedForecastService.getConstrainedForecastPlanningPeriods(gfc);
      this.constrainedForecastPlanningPeriods = convertPlanningPeriodArray(data.reverse());

      if (this.constrainedForecastPlanningPeriods.length > 0) {
        this.constrainedForecastPeriod = this.constrainedForecastPlanningPeriods[0];

        if (this.conceptDetails) {
          await this.getConstrainedData(
            String(this.conceptDetails.gfc),
            this.constrainedForecastPeriod.code
          );
          await this.fetchConstrainedForecastProgress(gfc);
        }
      } else {
        this.isConstrainedForecastLoading = false;
      }
    } catch (error) {
      console.log(
        'ConstrainedForecastViewScreenStore fetchConstrainedForecastPlanningPeriod => ',
        error
      );
    } finally {
      this.constrainedFOrecastPlanningPeriodLoading = false;
      this.isConstrainedForecastStatusLoading = false;
    }
  };

  setConstrainedForecastPeriod = (option: { label: string; code: string }) => {
    this.constrainedForecastPeriod = option;

    if (this.conceptDetails?.gfc) {
      this.getConstrainedData(String(this.conceptDetails?.gfc), option.code);
    }
  };

  getInvoiceSalesSummary = async (gfc: number) => {
    this.isInvoiceSalesLoading = true;
    try {
      const response = await unconstrainedForecastService.getSalesSummaryDetails(gfc);
      this.invoiceSalesSummary = response;
    } catch (error) {
      console.log('getInvoiceSalesSummary=> ', error);
    } finally {
      this.isInvoiceSalesLoading = false;
    }
  };

  fetchForecastGFCNotes = async (ndc11: string, signal?: AbortSignal) => {
    this.isConstrainedForecastNotesDataLoading = true;
    try {
      this.ConstrainedForecastNotesData = await unconstrainedForecastService.getNDC11Note(
        ndc11,
        'constrained',
        signal
      );
    } catch (error) {
      console.log('MSPlanStore fetchMSGFCNotes => ', error);
    } finally {
      this.isConstrainedForecastNotesDataLoading = false;
    }
  };

  getConstrainedData = async (ndc: string, planning_period: string) => {
    try {
      const response = await constrainedForecastService.getConstrainedForecast(
        ndc,
        planning_period
      );
      this.constrainedForecastData = response;
      this.fetchNDCHeadings();
    } catch (error) {
      console.log('getConstrainedData=> ', error);
    } finally {
      this.isConstrainedForecastLoading = false;
    }
  };

  fetchBulkUpdateNDCs = async () => {
    try {
      const data = await constrainedForecastService.getBulkNDCList();
      this.bulkUpdateDirectTodoNDC = data.plan_status.Direct.ToDo;
      this.bulkUpdateDirectCompletedNDC = data.plan_status.Direct.Completed;
      this.bulkUpdateIndirectTodoNDC = data.plan_status.Indirect.ToDo;
      this.bulkUpdateIndirectCompletedNDC = data.plan_status.Indirect.Completed;
    } catch (error) {
      console.log('fetchBulkUpdateNDCs=> ', error);
    } finally {
      this.isUnconstrainedForecastProgressLoading = false;
    }
  };

  fetchNDCHeadings = () => {
    this.directNDCheading = [];
    this.indirectNDCheading = [];

    for (const forecast of this.constrainedForecastData.forecasts) {
      if (forecast.SellingCategory.toLowerCase() === 'direct') {
        if (this.directNDCheading.filter(item => item.ndc === forecast.NDC11).length === 0) {
          this.directNDCheading.push({
            ndc: forecast.NDC11,
            packSize: String(forecast.PackSize),
          });
        }
      } else if (forecast.SellingCategory.toLowerCase() === 'indirect') {
        if (this.indirectNDCheading.filter(item => item.ndc === forecast.NDC11).length === 0) {
          this.indirectNDCheading.push({
            ndc: forecast.NDC11,
            packSize: String(forecast.PackSize),
          });
        }
      }
    }
    if (this.directNDCheading[0]) {
      this.fetchForecastGFCNotes(this.directNDCheading[0]?.ndc);
    }
  };

  bulkUpdateConstrainedForecast = async (ndc_list: string[], planning_period: string) => {
    try {
      this.isForecastBulkUpdating = true;
      const payload: {
        ndc_list: string[];
        planning_period: string;
        user: string;
      } = {
        ndc_list: ndc_list,
        planning_period: planning_period,
        user: 'frontend user',
      };
      await constrainedForecastService.bulkUpdateNDCs(payload);
    } catch (error) {
      console.log('bulkUpdateConstrainedForecast', error);
    } finally {
      this.fetchBulkUpdateNDCs();
      this.fetchForecastProgress();
      this.isForecastBulkUpdating = false;
      this.isBulkUpdateModalOpen = false;
      this.bulkUpdateNDCs = [];
    }
  };

  toggleBulkUpdateModal = () => {
    this.isBulkUpdateModalOpen = !this.isBulkUpdateModalOpen;
  };

  resetStore = () => {
    // Tab and layout state
    this.directcurrentNDCIndex = 0;
    this.indirectcurrentNDCIndex = 0;

    // Loading states
    this.isAllConceptLoading = false;
    this.isConstrainedForecastLoading = false;
    this.isConceptLoading = false;
    this.isConceptDetailsLoading = false;
    this.isInvoiceSalesLoading = false;
    this.isForecastBulkUpdating = false;
    this.isConstrainedForecastNotesDataLoading = false;
    this.isUnconstrainedForecastProgressLoading = false;
    this.isConstrainedForecastStatusLoading = false;
    this.constrainedFOrecastPlanningPeriodLoading = false;

    // Search related data
    this.filteredConceptsForSearch = [];
    this.allDrugMoleculesDetailsForSearch = [];
    this.filteredDrugMoleculesDetails = [];
    this.conceptSearchInput = '';
    this.lastNdcValueEntered = '';

    // Concept and contract details
    this.conceptDetails = {
      concept: '',
      gfc: 0,
      generic_rxcui: '',
      therapeutic_category: '',
      pharmacologic_class: '',
      va_class_name: '',
      des_schedule: '',
      duration_condition: '',
      Strength: '',
    };
    this.contractDetails = [
      {
        GFC: 0,
        ContractNumber: '',
        ContractType: '',
        StartDate: '',
        EndDate: '',
      },
    ];

    // NDC data
    this.directNDCheading = [];
    this.indirectNDCheading = [];

    // Forecast data
    this.constrainedForecastData = {
      GFC: 0,
      PlanningPeriod: '',
      forecasts: [
        {
          NDC11: '',
          PackSize: 0,
          SellingCategory: '',
          forecast_details: [
            {
              ForecastPeriod: '',
              Adjustment: 0,
              UnconstrainedForecast: 0,
              ConstrainedForecast: 0,
              Notes: '',
            },
          ],
        },
      ],
    };

    // Bulk update data
    this.isBulkUpdateModalOpen = false;
    this.bulkNDCdata = [];
    this.bulkUpdateDirectTodoNDC = [];
    this.bulkUpdateIndirectTodoNDC = [];
    this.bulkUpdateDirectCompletedNDC = [];
    this.bulkUpdateIndirectCompletedNDC = [];
    this.bulkUpdateNDCs = [];

    // Progress and notes data
    this.invoiceSalesSummary = [];
    this.ConstrainedForecastNotesData = [];
    this.percentProgress = { percentage: 0 };
    this.constrainedForecastStatus = false;
    this.constrainedForecastPlanningPeriods = [];
    this.constrainedForecastPeriod = { label: '', code: '' };
    this.forecastBtnLabel = 'New Forecast';
  };
}
